import { useEffect, useState } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useDash } from "./ctx";
import { History, Search, FileText, Scale, Settings2, UserCog, ShieldCheck, Megaphone, Activity, ChevronLeft, ChevronRight, ChevronDown, Sparkles, Table2, MessagesSquare } from "lucide-react";
import { V } from "@/lib/vocab";

const EVENT_META = {
  document_added: { label: "Document added", icon: FileText },
  document_updated: { label: "Document edited", icon: FileText },
  document_resummarized: { label: "AI summary regenerated", icon: FileText },
  rule_verified: { label: "Rule verified", icon: Scale },
  rule_dismissed: { label: "Rule dismissed", icon: Scale },
  config_updated: { label: "Assistant settings changed", icon: Settings2 },
  service_updated: { label: "Channels/service changed", icon: Settings2 },
  legal_updated: { label: "Legal & compliance changed", icon: ShieldCheck },
  member_invited: { label: "Teammate invited", icon: UserCog },
  broadcast_sent: { label: "Broadcast sent", icon: Megaphone },
  copilot_ask: { label: "Copilot question", icon: Sparkles },
  copilot_action: { label: "Copilot action", icon: Sparkles },
  copilot_action_undone: { label: "Copilot action undone", icon: Sparkles },
  copilot_budget_applied: { label: "Budget corrected", icon: Table2 },
  conversation_resolved: { label: "Conversation resolved", icon: MessagesSquare },
};
const ROLE_BADGE = {
  hoa_manager: { label: "Manager", cls: "bg-blue-100 text-blue-700" },
  board_member: { label: "Board", cls: "bg-violet-100 text-violet-700" },
  company_admin: { label: "Administrator", cls: "bg-amber-100 text-amber-700" },
  regional_manager: { label: "Portfolio", cls: "bg-amber-100 text-amber-700" },
  custom: { label: "Custom role", cls: "bg-teal-100 text-teal-700" },
  super_admin: { label: "Redline", cls: "bg-ink/10 text-ink/60" },
  platform_admin: { label: "Redline", cls: "bg-ink/10 text-ink/60" },
  onboarding_admin: { label: "Redline", cls: "bg-ink/10 text-ink/60" },
  operator: { label: "Redline", cls: "bg-ink/10 text-ink/60" },
};
const CATEGORIES = [["", "All activity"], ["copilot", "Copilot"], ["knowledge", "Knowledge & docs"], ["rules", "Rules"], ["settings", "Settings"], ["team", "Team"], ["messaging", "Messaging"], ["conversations", "Conversations"]];
const ROLES = [["", "Anyone"], ["manager", "Community managers"], ["board", "Board members"], ["staff", "Redline staff"]];
const PAGE_SIZES = [25, 50, 100];

const humanize = (ev) => EVENT_META[ev]?.label || (ev || "").replace(/_/g, " ").replace(/\bcommunity\b/g, V.lc("org")).replace(/\bresident\b/g, V.lc("person")).replace(/\b\w/g, (c) => c.toUpperCase());
const fmt = (iso) => { try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso; } };
const FilterSelect = ({ value, onChange, options, testid, title }) => (
  <div className="relative" title={title}>
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
      className="appearance-none rounded-full bg-white border border-sand-200 pl-3.5 pr-8 py-2 text-sm font-medium text-ink/80 outline-none focus:border-ink cursor-pointer">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
    <ChevronDown className="h-3.5 w-3.5 text-ink/40 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
  </div>
);

export default function ActivityLog() {
  const { tenantId } = useDash();
  const [data, setData] = useState(null);           // {items,total,page,pages,page_size}
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");            // debounced, server-side
  const [category, setCategory] = useState("");
  const [role, setRole] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => { const t = setTimeout(() => { setQuery(q.trim()); setPage(1); }, 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setPage(1); }, [tenantId, pageSize, category, role]);
  useEffect(() => {
    if (!tenantId) return;
    let alive = true;
    setData((d) => (d ? { ...d, loading: true } : null));
    api.get(`/tenants/${tenantId}/audit`, { params: { page, page_size: pageSize, q: query, category, role } })
      .then((r) => { if (alive) setData({ ...r.data, loading: false }); })
      .catch(() => { if (alive) setData({ items: [], total: 0, page: 1, pages: 1, page_size: pageSize, loading: false }); });
    return () => { alive = false; };
  }, [tenantId, page, pageSize, query, category, role]);

  const toggleExpand = (id) => setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const items = data?.items || [];
  const total = data?.total || 0;
  const pages = data?.pages || 1;
  const first = total ? (page - 1) * pageSize + 1 : 0;
  const last = Math.min(page * pageSize, total);

  const Pager = ({ where }) => total > 0 && (
    <div data-testid={`activity-pager-${where}`} className="flex items-center justify-between gap-3 flex-wrap px-4 sm:px-5 py-3 text-xs text-muted-foreground">
      <div data-testid={`activity-range-${where}`}>Showing <b className="text-ink">{first}–{last}</b> of <b className="text-ink">{total.toLocaleString()}</b>{query ? " matching" : ""}</div>
      <div className="flex items-center gap-2">
        {where === "bottom" && (
          <label className="flex items-center gap-1.5">Per page
            <select data-testid="activity-page-size" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
              className="rounded-lg border border-sand-200 bg-white px-2 py-1 text-xs outline-none focus:border-ink">
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        )}
        <button data-testid={`activity-prev-${where}`} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
          className="h-8 w-8 rounded-lg border border-sand-200 bg-white flex items-center justify-center hover:border-ink disabled:opacity-40 disabled:cursor-not-allowed"><ChevronLeft className="h-4 w-4" /></button>
        <span data-testid={`activity-page-${where}`} className="tabular-nums">Page <b className="text-ink">{page}</b> of <b className="text-ink">{pages}</b></span>
        <button data-testid={`activity-next-${where}`} disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))}
          className="h-8 w-8 rounded-lg border border-sand-200 bg-white flex items-center justify-center hover:border-ink disabled:opacity-40 disabled:cursor-not-allowed"><ChevronRight className="h-4 w-4" /></button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="activity-log-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <PageTitle title={<><History className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> Activity Log</>} about={<>Every action across this {V.lc("org")} — Copilot questions, knowledge, rules, settings and messaging — who did it and when. Filter by type or person; search spans the whole history.</>} />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <FilterSelect value={category} onChange={setCategory} options={CATEGORIES} testid="activity-filter-type" />
          <FilterSelect value={role} onChange={setRole} options={ROLES} testid="activity-filter-role" title="Filter by who did it. Note: entries from before this update may not show a person." />
          <div className="relative">
            <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="activity-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search activity…"
              className="rounded-full bg-white border border-sand-200 pl-9 pr-4 py-2 text-sm outline-none focus:border-ink w-56" />
          </div>
        </div>
      </div>

      <div className={`bg-white rounded-2xl border border-sand-200 overflow-hidden transition-opacity ${data?.loading ? "opacity-60" : ""}`}>
        <Pager where="top" />
        {data === null ? (
          <div className="flex items-center justify-center py-16"><div className="h-6 w-6 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="px-6 py-16 text-center text-sm text-muted-foreground flex flex-col items-center gap-3" data-testid="activity-empty">
            <div className="h-12 w-12 rounded-2xl bg-sand-100 flex items-center justify-center"><Activity className="h-6 w-6 text-ink/30" /></div>
            {(query || category || role) ? "No activity matches these filters." : "No activity recorded yet. Changes you make will show up here."}
          </div>
        ) : (
          <div className="divide-y divide-sand-100 border-t border-sand-100" data-testid="activity-rows">
            {items.map((l) => {
              const Icon = EVENT_META[l.event]?.icon || Activity;
              const badge = ROLE_BADGE[l.role];
              const answer = l.meta?.answer;
              const isOpen = expanded.has(l.id);
              const isAsk = l.event === "copilot_ask";
              return (
                <div key={l.id} data-testid={`activity-row-${l.id}`} className="flex items-start gap-3 px-4 sm:px-5 py-3.5 hover:bg-sand-50 transition-colors">
                  <span className="h-9 w-9 rounded-xl bg-redline-50 flex items-center justify-center shrink-0 mt-0.5"><Icon className="h-4 w-4 text-redline-600" strokeWidth={1.75} /></span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-ink">{humanize(l.event)}</span>
                        {badge && <span data-testid={`activity-role-${l.id}`} className={`text-[10px] font-bold uppercase tracking-wide rounded-full px-1.5 py-0.5 ${badge.cls}`}>{badge.label}</span>}
                        {isAsk && l.meta?.ok === false && <span className="text-[10px] font-bold uppercase tracking-wide rounded-full px-1.5 py-0.5 bg-rose-100 text-rose-700">No answer</span>}
                      </div>
                      <div className="sm:hidden shrink-0 text-right text-[11px] text-muted-foreground leading-tight">
                        <div className="font-medium text-ink/70">{l.user || "system"}</div>
                        <div>{fmt(l.created_at)}</div>
                      </div>
                    </div>
                    {l.detail && <div className="text-sm text-muted-foreground mt-0.5 break-words [overflow-wrap:anywhere]">{isAsk ? <><span className="font-semibold text-ink/70">Asked:</span> “{l.detail}”</> : l.detail}</div>}
                    {answer && (
                      <div className="mt-1">
                        <button data-testid={`activity-answer-toggle-${l.id}`} onClick={() => toggleExpand(l.id)}
                          className="text-xs font-semibold text-redline-600 hover:text-redline-700 inline-flex items-center gap-1">
                          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} /> {isOpen ? "Hide answer" : "Show answer"}
                        </button>
                        {isOpen && (
                          <div data-testid={`activity-answer-${l.id}`} className="mt-1.5 text-sm text-ink/80 bg-sand-50 border border-sand-200 rounded-xl px-3 py-2 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{answer}</div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="hidden sm:block shrink-0 text-right">
                    <div className="text-xs font-medium text-ink/70">{l.user || "system"}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{fmt(l.created_at)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {items.length > 0 && <div className="border-t border-sand-100"><Pager where="bottom" /></div>}
      </div>
    </div>
  );
}
